"use client";

import {
  CheckCircleIcon,
  ExclamationCircleIcon,
} from "@heroicons/react/24/outline";

import { Consultation } from "@/app/_components/consultation-card";
import { Error } from "@/app/_components/error";
import { Loading } from "../loading";

import { useNotice } from "@/queries/use-notice";

import type { NoticeItem } from "../notice.types";

export const Resumo = ({ id, field, emptyElement }: NoticeItem) => {
  const { data, isPending, error, refetch } = useNotice({ id, field });

  if (isPending) return <Loading />;

  if (error) return <Error reset={refetch} />;

  const notice = data?.analise.data;

  if (!notice) {
    return (
      emptyElement ?? (
        <p className="text-center text-base text-muted-foreground w-4/6">
          A análise do edital provavelmente não está disponível. Se você tiver
          alguma dúvida, não hesite em entrar em contato conosco.
        </p>
      )
    );
  }

  return (
    <>
      <div className="flex flex-col sm:flex-row gap-10 sm:gap-6">
        <Consultation.Card
          title="Resumo Geral da Análise"
          description="Avaliação geral do edital e recomendações"
        >
          <div className="flex flex-col gap-2">
            <h4 className="font-medium">Nível de Segurança Jurídica:</h4>
            <p className="text-muted-foreground">
              {notice.resumo_geral.nivel_seguranca_juridica}
            </p>
          </div>
          <div className="flex flex-col gap-2">
            <h4 className="font-medium">Observações Gerais:</h4>
            <p className="text-muted-foreground">
              {notice.resumo_geral.observacoes_gerais}
            </p>
          </div>
          <div className="flex flex-col gap-2">
            <h4 className="font-medium">Recomendação Final:</h4>
            <p className="text-muted-foreground">
              {notice.resumo_geral.recomendacao_final}
            </p>
          </div>
          <div className="flex flex-col gap-2">
            <h4 className="font-medium">Pontos críticos de maior atenção:</h4>
            <div className="flex flex-col sm:grid sm:grid-cols-2 gap-4">
              {notice.riscos_e_pontos_criticos
                .filter((risco) => risco.criticidade === "Alta")
                .map((risco, index) => (
                  <div
                    key={index}
                    className="p-3 bg-red-50/50 border border-red-50 rounded-md flex flex-col gap-2"
                  >
                    <h4 className="font-medium flex items-start gap-2">
                      <ExclamationCircleIcon className="h-5 w-5 text-red-400 mt-0.5 shrink-0" />
                      {risco.titulo}
                    </h4>
                    <p className="text-muted-foreground">{risco.descricao}</p>
                  </div>
                ))}
            </div>
          </div>
        </Consultation.Card>
      </div>
      <Consultation.Card
        title="Parecer Técnico Conclusivo"
        description="Análise conclusiva sobre a oportunidade"
      >
        <div className="flex flex-col gap-2">
          <h4 className="font-medium">Segurança Jurídica:</h4>
          <p className="text-muted-foreground">
            {notice.parecer_tecnico_conclusivo.avaliacao_geral}
          </p>
        </div>
      </Consultation.Card>
      <Consultation.Card
        title="Cuidados Adicionais Recomendados"
        description="Medidas preventivas antes de participar do leilão"
      >
        <ul className="flex flex-col gap-3">
          {notice.parecer_tecnico_conclusivo.cuidados_adicionais.map(
            (cuidado, index) => (
              <li key={index} className="flex items-start gap-2">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mt-0.5 shrink-0" />
                <span>{cuidado}</span>
              </li>
            )
          )}
        </ul>
      </Consultation.Card>
    </>
  );
};
